/**
 * Shared Banner — carousel controller for the promo banner strip.
 *
 * Carousel mode: always rotates when multiple slides present.
 * Multi mode: rotates on mobile only (< 768px), shows all side-by-side on desktop.
 */

const DEFAULT_ROTATE_INTERVAL = 8000;
const MOBILE_BREAKPOINT = '(max-width: 767px)';

class BannerCarousel {
  private readonly container: HTMLElement;
  private readonly slides: HTMLElement[];
  private readonly track: HTMLElement | null;
  private readonly prevBtn: HTMLElement | null;
  private readonly nextBtn: HTMLElement | null;
  private readonly pauseBtn: HTMLElement | null;
  private readonly rotateInterval: number;
  private readonly isMultiMode: boolean;
  private currentIndex = 0;
  private autoRotateTimer: ReturnType<typeof setInterval> | null = null;
  private isPaused = false;
  private isUserPaused = false;
  private isActive = false;
  private hasCompletedFirstLoop = false;
  private mediaQuery: MediaQueryList | null = null;
  private boundMediaHandler: ((e: MediaQueryListEvent) => void) | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.slides = Array.from(
      container.querySelectorAll<HTMLElement>('[data-banner-id]')
    );
    this.track = container.querySelector<HTMLElement>('[data-banner-track]');
    this.prevBtn = container.querySelector<HTMLElement>('[data-banner-prev]');
    this.nextBtn = container.querySelector<HTMLElement>('[data-banner-next]');
    this.pauseBtn = container.querySelector<HTMLElement>('[data-banner-pause]');
    this.rotateInterval =
      parseInt(container.getAttribute('data-banner-interval') || '', 10) ||
      DEFAULT_ROTATE_INTERVAL;
    this.isMultiMode = container.getAttribute('data-banner-mode') === 'multi';

    if (this.slides.length <= 1) return;

    if (this.isMultiMode) {
      // Multi mode: only carousel on mobile
      this.mediaQuery = window.matchMedia(MOBILE_BREAKPOINT);
      this.boundMediaHandler = (e: MediaQueryListEvent) => {
        if (e.matches) this.activate();
        else this.deactivate();
      };
      this.mediaQuery.addEventListener('change', this.boundMediaHandler);
      if (this.mediaQuery.matches) {
        this.activate();
      } else {
        // Desktop: show all slides, remove aria-hidden and hidden class
        for (const slide of this.slides) {
          slide.classList.remove('hidden');
          slide.removeAttribute('aria-hidden');
        }
      }
    } else {
      // Carousel mode: always active
      this.activate();
    }
  }

  private activate(): void {
    if (this.isActive) return;
    this.isActive = true;
    // Hide non-active slides from screen readers when carousel is active
    for (let i = 1; i < this.slides.length; i++) {
      this.slides[i].setAttribute('aria-hidden', 'true');
    }
    this.showSlide(0);
    this.bindEvents();
    this.startAutoRotate();
  }

  private deactivate(): void {
    if (!this.isActive) return;
    this.isActive = false;
    this.stopAutoRotate();
    // Show all slides for desktop layout
    for (const slide of this.slides) {
      slide.classList.remove('hidden');
      slide.removeAttribute('aria-hidden');
    }
  }

  private bindEvents(): void {
    this.prevBtn?.addEventListener('click', this.handlePrev);
    this.nextBtn?.addEventListener('click', this.handleNext);
    this.pauseBtn?.addEventListener('click', this.handlePauseToggle);
    this.container.addEventListener('mouseenter', this.handlePause);
    this.container.addEventListener('mouseleave', this.handleResume);
    this.container.addEventListener('focusin', this.handlePause);
    this.container.addEventListener('focusout', this.handleResume);
  }

  private handlePrev = (): void => {
    this.setLiveRegion('polite');
    this.goTo(this.currentIndex - 1);
    this.resetAutoRotate();
  };

  private handleNext = (): void => {
    this.setLiveRegion('polite');
    this.goTo(this.currentIndex + 1);
    this.resetAutoRotate();
  };

  private handlePause = (): void => {
    this.isPaused = true;
  };
  private handleResume = (): void => {
    if (!this.isUserPaused) {
      this.isPaused = false;
    }
  };

  private handlePauseToggle = (): void => {
    this.isUserPaused = !this.isUserPaused;
    this.isPaused = this.isUserPaused;

    if (this.pauseBtn) {
      const icon = this.pauseBtn.querySelector('use');
      this.pauseBtn.setAttribute('aria-pressed', String(this.isUserPaused));
      if (this.isUserPaused) {
        if (icon) icon.setAttribute('href', '#chevron-right');
      } else {
        if (icon) icon.setAttribute('href', '#pause');
      }
    }
  };

  private setLiveRegion(value: 'polite' | 'off'): void {
    this.track?.setAttribute('aria-live', value);
  }

  private showSlide(index: number): void {
    for (let i = 0; i < this.slides.length; i++) {
      const slide = this.slides[i];
      if (i === index) {
        slide.classList.remove('hidden');
        slide.removeAttribute('aria-hidden');
      } else {
        slide.classList.add('hidden');
        slide.classList.remove('md:block');
        slide.setAttribute('aria-hidden', 'true');
      }
    }
    this.currentIndex = index;

    // Update container background color for carousel per-slide bgColor
    const activeBg = this.slides[index]?.getAttribute('data-banner-bg');
    if (activeBg) {
      this.container.classList.remove(
        'bg-highlight-blue',
        'bg-highlight-green',
        'bg-highlight-pink',
        'bg-highlight-yellow',
        'bg-highlight-grey'
      );
      this.container.classList.add(activeBg);
    }
  }

  private goTo(index: number): void {
    let next = index;
    if (next < 0) next = this.slides.length - 1;
    if (next >= this.slides.length) next = 0;
    this.showSlide(next);
  }

  private startAutoRotate(): void {
    this.autoRotateTimer = setInterval(() => {
      if (!this.isPaused && this.isActive) {
        const nextIndex = (this.currentIndex + 1) % this.slides.length;

        // After first full loop, stop announcing auto-rotation
        if (this.hasCompletedFirstLoop) {
          this.setLiveRegion('off');
        } else if (nextIndex === 0) {
          // About to wrap back to first slide — first loop is done
          this.hasCompletedFirstLoop = true;
          this.setLiveRegion('off');
        }

        this.goTo(nextIndex);
      }
    }, this.rotateInterval);
  }

  private stopAutoRotate(): void {
    if (this.autoRotateTimer) {
      clearInterval(this.autoRotateTimer);
      this.autoRotateTimer = null;
    }
  }

  private resetAutoRotate(): void {
    this.stopAutoRotate();
    this.startAutoRotate();
  }
}

function initBanners(): void {
  const banners = document.querySelectorAll<HTMLElement>('[data-promo-banner]');
  for (const banner of banners) {
    new BannerCarousel(banner);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBanners);
} else {
  initBanners();
}

export { BannerCarousel };
